import _, { Dictionary } from 'lodash';
import { T } from 'src/shared/helpers';
import { ProductProvider } from '../../state/queries/__generated__/productFragment_model.graphql';
import { productProvider_product$data } from '../../state/queries/__generated__/productProvider_product.graphql';
import { productProvider_retailProduct$data, ProviderRegionStock } from '../../state/queries/__generated__/productProvider_retailProduct.graphql';

export const locationsNames: Dictionary<string> = {
    US: T._('USA', 'Admin'),
    EU: T._('Europe', 'Admin'),
    MX: T._('Mexico', 'Admin'),
    JP: T._('Japan', 'Admin'),
    AU: T._('Australia', 'Admin'),
    BR: T._('Brazil', 'Admin'),
    CA: T._('Canada', 'Admin'),
    EU_LV: T._('Europe/Latvia', 'Admin'),
    EU_ES: T._('Europe/Spain', 'Admin'),
    CN: T._('China', 'Admin'),
    UK: T._('United Kingdom', 'Admin'),
    _UNDEFINED: T._('Undefined', 'Admin')
} as const;

/**
 * Get all options available for a provider product
 * @param product The provider product graphql model
 */
export function getOptions(product: productProvider_product$data) {
    let options = product.options.map((option) => {
        return {
            optionID: option.optionID,
            title: option.title,
            type: option.type,
            values: option.values.map((x) => x.value)
        };
    });

    if (product.variants.some((variant) => variant.canRotate)) {
        options = [
            {
                optionID: 'Rotation_Orientation',
                title: 'Orientation',
                type: 'radio',
                values: ['Horizontal', 'Vertical']
            },
            ...options
        ];
    }

    return options;
}

/**
 * Get all sizes available for a provider product
 * @param product The provider product graphql model
 */
export function getSizes(product: productProvider_product$data, selectedLocations: ProviderRegionStock[], provider: ProductProvider) {
    const sizes: string[] = [];

    for (const variant of product.variants) {
        if (
            variant.size &&
            variant.size != '' &&
            sizes.indexOf(variant.size) == -1 &&
            (provider != 'PRINTFUL' || variant.availabilityStatus.some((status) => selectedLocations.indexOf(status.region) !== -1))
        )
            sizes.push(variant.size);
    }

    if (sizes.length == 0) sizes.push('Default');

    return sizes;
}

/**
 * Get all colors available for a provider product
 * @param product The provider product graphql model
 */
export function getColors(product: productProvider_product$data, selectedLocations: ProviderRegionStock[], provider: ProductProvider) {
    const colors: { name: string; code: string }[] = [];

    for (const variant of product.variants) {
        if (
            variant.colorCode &&
            variant.colorName &&
            colors.findIndex((color) => color.name == variant.colorName) == -1 &&
            (provider != 'PRINTFUL' || variant.availabilityStatus.some((status) => selectedLocations.indexOf(status.region) !== -1))
        )
            colors.push({
                name: variant.colorName,
                code: variant.colorCode
            });
    }

    return colors.sort((a, b) => (a.name.toLowerCase() === 'white' || a.code.toLowerCase() == '#ffffff' ? -1 : 1));
}

/**
 * Get all locations available for a provider product
 * @param product The provider product graphql model
 */
export function getLocations(product: productProvider_product$data) {
    const locations: ProviderRegionStock[] = [];

    for (const variant of product.variants) {
        for (const availabilityStatus of variant.availabilityStatus) {
            if (locations.indexOf(availabilityStatus.region) === -1) locations.push(availabilityStatus.region);
        }
    }

    return locations;
}

/**
 * Get all sides available for a provider product
 * @param product The provider product graphql model
 */
export interface ProductProviderProductSide {
    id: string;
    name: string;
    imageUrl: string | null;
    mandatory: boolean;
    conflicts: string[];
}

export function getSides(product: productProvider_product$data) {
    const sides: Readonly<ProductProviderProductSide>[] = [];

    for (const { key: sideKey, value: sideName } of product.sidesNames) {
        if (sideName && sideKey) {
            const info = product.sides.find((x) => x.side.toLowerCase() == sideName.toLowerCase() || x.side.toLowerCase() == sideKey.toLowerCase());
            const conflicts =
                product.conflictingSides.find((x) => x.side.toLowerCase() == sideName.toLowerCase() || x.side.toLowerCase() == sideKey.toLowerCase())
                    ?.conflicts ?? [];

            if (info) {
                sides.push({
                    id: sideKey,
                    name: sideName,
                    mandatory: info.mandatory,
                    imageUrl: info.imageUrl,
                    conflicts: conflicts
                } as Readonly<ProductProviderProductSide>);
            }
        }
    }

    return sides;
}

/**
 * Get all selected variants by combining colors and sizes
 * @param product The provider product graphql model
 * @param colors The selected colors
 * @param sizes The selected sizes
 */

type Variant = productProvider_product$data['variants'][number] | productProvider_retailProduct$data['variants'][number];

export function getVariants(product: productProvider_product$data, colors: string[], sizes: string[]) {
    const variants: Variant[] = [];

    for (const size of sizes) {
        // Check for posters that don't have a color
        if (colors.length == 0) {
            const variant = product.variants.find((v) => v.size == size);
            if (variant) variants.push(variant);
        } else {
            for (const color of colors) {
                const variant = product.variants.find((v) => v.size == size && v.colorName == color);
                if (variant) variants.push(variant);
            }
        }
    }

    return variants;
}

/**
 * Get all prices per variant
 * @param product The provider product graphql model
 * @param variants The selected variants from @link getVariants
 */
export interface Price {
    size: string;
    priceMin: number;
    priceMax: number;
    retailMin: number;
    retailMax: number;
}

export function getPrices(variants: Variant[], profit: number) {
    const prices: Price[] = [];

    const groupedVariants = _.groupBy(variants, (v) => v.size);
    _.keys(groupedVariants).forEach((size) => {
        const priceMinVariant = _.minBy(groupedVariants[size], (v) => parseFloat(v.price));
        const priceMaxVariant = _.maxBy(groupedVariants[size], (v) => parseFloat(v.price));

        if (priceMinVariant && priceMaxVariant) {
            const priceMin = parseFloat(priceMinVariant.price);
            const priceMax = parseFloat(priceMaxVariant.price);

            const retailMin = Math.max(Math.round((priceMin + (priceMin / 100) * profit) * 2) / 2, priceMin);
            const retailMax = Math.max(Math.round((priceMax + (priceMax / 100) * profit) * 2) / 2, priceMax);

            prices.push({
                size,
                priceMin,
                priceMax,
                retailMin,
                retailMax
            });
        }
    });

    return prices;
}

export function renderRange(number1: number, number2: number, priceFormatter: Intl.NumberFormat) {
    if (number1 === number2) return priceFormatter.format(number1);
    else return `${priceFormatter.format(number1)} - ${priceFormatter.format(number2)}`;
}

export function getImportMutationData(
    product: productProvider_product$data,
    name: string,
    description: string,
    options: [string, string][],
    variants: Variant[],
    sides: string[],
    profit: number
) {
    const data = {
        productID: product.generalInfo.productID,
        variantIDs: variants.map((variant) => variant.variantID),
        sides: sides.length > 0 ? sides : _.keys(product.sidesNames),
        retailProfitPercentage: profit,
        selectedOptions: options
            .filter((option) => option[0] != 'Rotation_Orientation')
            .map(([id, value]) => {
                return {
                    id,
                    value
                };
            }),
        verticalRotation: options.filter((option) => option[0] == 'Rotation_Orientation' && option[1] == 'Vertical').length > 0,
        productEcommerce: {
            productTitle: name,
            productDescription: description
        }
    };

    return data;
}
